import { useMemo, useState } from "react";
import { FilterOption } from "@/components/ui/filter-select";
import { IStoreFilterableOption } from "@/services/store/select-options";
import { NormalizedStore } from "@/@types/campaign";

const ALL_OPTION: FilterOption = { value: "", label: "Todos" };

export function useStoreSelectionFilter(
  stores: IStoreFilterableOption[],
  selectedCodes: number[],
) {
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedRegional, setSelectedRegional] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const normalizedStores = useMemo<NormalizedStore[]>(
    () =>
      stores.map(store => ({
        code: parseInt(store.value),
        name: store.label,
        state: store.state ?? "",
        city: store.city ?? "",
        regionalId: store.regionalId,
      })),
    [stores],
  );

  const stateOptions = useMemo<FilterOption[]>(() => {
    const distinct = Array.from(
      new Set(normalizedStores.map(store => store.state).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
    return distinct.map(uf => ({ value: uf, label: uf }));
  }, [normalizedStores]);

  const toggleState = (uf: string): void => {
    setSelectedStates((current: string[]) =>
      current.includes(uf)
        ? current.filter((state: string) => state !== uf)
        : [...current, uf],
    );
  };

  const regionalOptions = useMemo<FilterOption[]>(() => {
    const byId = new Map<number, string>();
    stores.forEach(store => {
      if (store.regionalId != null && store.regionalName) {
        byId.set(store.regionalId, store.regionalName);
      }
    });
    const distinct = Array.from(byId.entries())
      .map(([id, name]) => ({ value: String(id), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [ALL_OPTION, ...distinct];
  }, [stores]);

  const filteredStores = useMemo<NormalizedStore[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    return normalizedStores
      .filter(store => {
        const matchesState =
          !selectedStates.length || selectedStates.includes(store.state);
        const matchesRegional =
          !selectedRegional || String(store.regionalId) === selectedRegional;
        const matchesSearch =
          !term ||
          store.name.toLowerCase().includes(term) ||
          store.code.toString().includes(term);
        return matchesState && matchesRegional && matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [normalizedStores, selectedStates, selectedRegional, searchTerm]);

  const selectAllFiltered = (): number[] =>
    Array.from(new Set([...selectedCodes, ...filteredStores.map(store => store.code)]));

  const allFilteredSelected = useMemo(
    () =>
      filteredStores.length > 0 &&
      filteredStores.every(store => selectedCodes.includes(store.code)),
    [filteredStores, selectedCodes],
  );

  const toggleAllFiltered = (): number[] => {
    if (allFilteredSelected) {
      const filteredCodes = new Set(filteredStores.map(store => store.code));
      return selectedCodes.filter(code => !filteredCodes.has(code));
    }
    return selectAllFiltered();
  };

  const clearFilters = () => {
    setSelectedStates([]);
    setSelectedRegional("");
    setSearchTerm("");
  };

  const hasActiveFilters =
    selectedStates.length > 0 || Boolean(selectedRegional) || Boolean(searchTerm.trim());

  return {
    normalizedStores,
    stateOptions,
    regionalOptions,
    filteredStores,
    selectedStates,
    toggleState,
    setSelectedStates,
    selectedRegional,
    setSelectedRegional,
    searchTerm,
    setSearchTerm,
    selectAllFiltered,
    toggleAllFiltered,
    allFilteredSelected,
    clearFilters,
    hasActiveFilters,
  };
}
